import * as React from 'react';
import { Box, Dialog, DialogActions, DialogContent, DialogContentText, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import InfoIcon from 'assets/images/InfoIcon.svg';
import NormalButton from 'components/system/atoms/buttons/NormalButton';
import OutlineTextBox from 'components/system/atoms/textBoxs/OutlineTextBox';
import HttpConnection from 'utils/httpConnection';
import MessageHelper from 'utils/message.helper';
import { REST_API } from 'constants/apiUrls';
import { handleCallFrontError } from 'redux/slices/commonSlice';
import MSG from 'constants/messages';
import ConfirmPolicyDialog from './ConfirmPolicyDialog';
import InfoDialog from './InfoDialog';

/* ************ Style ************ */
const useStyles = makeStyles()((theme) => ({
  paper: {
    backgroundColor: theme.palette.common.black,
    minHeight: '600px',
    width: '640px',
  },
  content: {
    alignItems: 'start',
    padding: '40px 8px 0px 40px ',
  },
  contentTop: {
    display: 'flex',
  },
  defaultBox: {
    margin: '8px 0 0 24px',
  },
  defaultText: {
    color: theme.palette.common.white,
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    fontSize: '21px',
  },
  action: {
    padding: '0px 40px 16px 40px ',
    display: 'flex',
    justifyContent: 'space-between',
  },
  detailmsg: {
    color: theme.palette.common.white,
    fontSize: 24,
    overflow: 'hidden',
  },
  buttonIcon: {
    height: 80,
    width: 200,
    fontSize: 28,
    borderRadius: 12,
  },
  buttonOk: {
    backgroundColor: '#FF3E3E',
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: '#FF3E3E',
    },
    '&:active': {
      backgroundColor: '#FF7070',
    },
  },
  buttonCancel: {
    backgroundColor: '#BCBCBC',
    color: theme.palette.common.black,
    '&:hover': {
      backgroundColor: '#BCBCBC',
    },
    '&:active': {
      backgroundColor: '#D3D3D3',
    },
  },
  twoAuthSetting: {
    color: theme.palette.common.white,
    fontSize: 24,
    alignItems: 'center',
    marginTop: '12px',
  },
  radioGroup: {
    display: 'flex',
    gap: '24px',
    color: theme.palette.common.white,
    fontSize: '24px',
  },
  settingBox: {
    color: theme.palette.common.white,
    fontSize: 24,
    marginTop: '12px',
  },
  textBox: {
    width: '95%'
  },
  passText: {
    width: '100%',
  },
  contentText: { fontSize: '24px' },
}));

type DialogProps = {
  open: boolean;
  msg: string;
  detailMsg?: string;
  okBtnLabel?: string;
  cancelBtnLabel?: string;
  onOk: () => void;
  onCancel: () => void;
  children?: React.ReactNode;
  addClass?: {
    contentBox?: string;
    contentText?: string;
  };
  setParentOpen?: (open: boolean) => void;
  userId?: string;
  setInputTwoAuth: (twoAuth: string, email: string) => void;
  srcMode?: number;
  versionNo?: number;
  twoAuthProps?: string;
};

const InputTextDialog: React.FC<React.PropsWithChildren<DialogProps>> = ({
  open,
  msg,
  detailMsg,
  onOk,
  okBtnLabel = '更新',
  onCancel,
  cancelBtnLabel = 'キャンセル',
  children,
  addClass,
  setParentOpen,
  userId,
  setInputTwoAuth,
  srcMode,
  versionNo,
  twoAuthProps
}) => {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const SRC_MODE = {
    NEW: 0, // 新規作成画面
    EDIT: 1, // 編集画面
    REFER: 2, // 参照画面
  };

const [twoAuth, setTwoAuth] = React.useState<string>(twoAuthProps === '0' ? 'deny' : 'allow');
  const [email, setEmail] = React.useState<string>('');
  const [confirmEmail, setConfirmEmail] = React.useState<string>('');
  const [password, setPassword] = React.useState<string>('');
  const [policyModal, setPolicyModal] = React.useState<boolean>(false);
  const [openUpdateSuccess, setOpenUpdateSuccess] = React.useState<boolean>(false);
  const myUserInfo = useAppSelector((state) => state.common.userInfo);

  /* ************ useEffect ************ */
  React.useEffect(() => {
  if (twoAuthProps !== undefined) {
    setTwoAuth(twoAuthProps === '0' ? 'deny' : 'allow');
  }
}, [twoAuthProps]);

  /* ************ Event ************ */
  const handleChangeEmailInput = (ev: React.ChangeEvent<HTMLInputElement>): void => {
    // Handle input change
    setEmail(ev.target.value);
  }

  const handleChangeConfirmEmailInput = (ev: React.ChangeEvent<HTMLInputElement>): void => {
    // Handle input change
    setConfirmEmail(ev.target.value);
  }

  const handleChangePasswordInput = (ev: React.ChangeEvent<HTMLInputElement>): void => {
    // Handle input change
    setPassword(ev.target.value);
  }

  // バリデーションチェック関数
  const validate = (): boolean => {
    // メールアドレス形式かどうかのチェック時に使用する正規表現
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (email === '') {
      dispatch(handleCallFrontError({errors: { key: [MessageHelper.messageFormat(MSG.ERR610)] },}),);
      return false; // バリデーションエラーの場合は false を返す
    }
    if (!emailPattern.test(email)) {
      dispatch(handleCallFrontError({errors: { key: [MessageHelper.messageFormat(MSG.ERR611)] },}),);
      return false;
    }
    if (email !== confirmEmail) {
      dispatch(handleCallFrontError({errors: { key: [MessageHelper.messageFormat(MSG.ERR609)] },}),);
      return false;
    }
    // 255文字以上の場合はエラー
    if (email.length > 255) {
      dispatch(handleCallFrontError({errors: { key: [MessageHelper.messageFormat(MSG.INF111, '255')] },}),);
      return false;
    }
    // パスワードが空の場合はエラー
    if (password === '') {
      dispatch(handleCallFrontError({errors: { key: [MessageHelper.messageFormat(MSG.INF119)] },}),);
      return false; // バリデーションエラーの場合は false を返す
    }
  
    return true; // バリデーション成功の場合は true を返す
  };

  // 下位権限者に対する編集時のバリデーションチェック
  const otherValidate = (): boolean => {
    // メールアドレス形式かどうかのチェック時に使用する正規表現
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    // パスワードが空の場合はエラー
    if (password === '') {
      dispatch(handleCallFrontError({errors: { key: [MessageHelper.messageFormat(MSG.INF119)] },}),);
      return false; // バリデーションエラーの場合は false を返す
    }
    if (email === '') {
      return true;
    }
    if (email !== '' && !emailPattern.test(email)) {
      dispatch(handleCallFrontError({errors: { key: [MessageHelper.messageFormat(MSG.ERR611)] },}),);
      return false;
    }
    if (email !== '' && email !== confirmEmail) {
      dispatch(handleCallFrontError({errors: { key: [MessageHelper.messageFormat(MSG.ERR609)] },}),);
      return false;
    }
    // 255文字以上の場合はエラー
    if (email.length > 255) {
      dispatch(handleCallFrontError({errors: { key: [MessageHelper.messageFormat(MSG.INF111, '255')] },}),);
      return false;
    }
    return true;
  }

  // 状態を拒否状態にした場合の処理
  const checkDeny = (value: string) => {
    setTwoAuth(value);
    setEmail('');
    setConfirmEmail('');
  };

  // 同意ボタンキャンセル時
  const handlePolicyCancel = (): void => {
    setPolicyModal(false);
    if (setParentOpen) {
      setParentOpen(true);
    }
  };

  /* ************ API ************ */
  // パスワード確認API
  const onUpdate = async() => {
    // バリデーションチェック(自身のアカウント編集時)
    if (srcMode === SRC_MODE.REFER && twoAuth === 'allow' && !validate()) return;
    // バリデーションチェック(他人のアカウント編集時)
    if (twoAuth === 'allow' && !otherValidate()) return;
    // 停止時のバリデーションチェック
    if (twoAuth === 'deny' && password === '') {
      dispatch(handleCallFrontError({errors: { key: [MessageHelper.messageFormat(MSG.INF119)] },}),);
      return;
    }

    const http = new HttpConnection({ dispatch });
    await http
      .post<boolean>(REST_API.MAINTENANCE.CHECK_PASSWORD, {userId: myUserInfo?.userId, password: password})
      .then((data) => {
          if (data) {
            if (setParentOpen) {
              setParentOpen(false);
            }
            if (twoAuth === 'deny') {
              handleOkUpdateConfirmDlg();
              return;
            }
            setPolicyModal(true);
          }
      });
  };

  // 二段階認証更新API
  const handleOkUpdateConfirmDlg = async () => {
    if (userId === undefined) {
      setInputTwoAuth(twoAuth, email);
      setPolicyModal(false);
      setOpenUpdateSuccess(true);
      setPassword('');
      setEmail('');
      setConfirmEmail('');
      return;
    }
    const http = new HttpConnection({ dispatch });
    await http
      .post<boolean>(REST_API.MAINTENANCE.UPDATE_TWO_AUTH, {userId: userId, email: email, twoAuth: twoAuth, versionNo: versionNo})
      .then((data) => {
          if (data) {
            setPolicyModal(false);
            setOpenUpdateSuccess(true);
            setPassword('');
            setEmail('');
            setConfirmEmail('');
            onOk();
          }
      });
  };

  return (
    <>
      <Dialog
        classes={{
          paper: classes.paper,
        }}
        open={open}
        onClose={onCancel}
        maxWidth={false}>
        <DialogContent
          classes={{
            root: classes.content,
          }}>
          <Box className={classes.contentTop}>
            <img src={InfoIcon} alt="Info" />
            <Box className={`${classes.defaultBox} ${addClass?.contentBox}`}>
              <DialogContentText className={`${classes.defaultText} ${addClass?.contentText}`}>{msg}</DialogContentText>
              {detailMsg && (
                <DialogContentText
                  classes={{
                    root: classes.detailmsg,
                  }}>
                  {detailMsg}
                </DialogContentText>
              )}
              {children}
            </Box>
          </Box>
          <Box className={classes.twoAuthSetting}>
            <Box>二段階認証</Box>
            <RadioGroup
              row
              className={classes.radioGroup}
              value={twoAuth}
              defaultValue={twoAuth}
              name="twoAuthOptions"
            >
              <FormControlLabel
                onChange={(e) => setTwoAuth('allow')}
                value="allow"
                control={<Radio sx={{ color: 'white', '&.Mui-checked': { color: 'red', } }} />}
                label="許可"
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: '21px',
                  },
                }}
              />
              <FormControlLabel
                onChange={() => checkDeny('deny')}
                value="deny"
                control={<Radio sx={{ color: 'white', '&.Mui-checked': { color: 'red' } }} />}
                label="停止"
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: '21px',
                  },
                }}
              />
            </RadioGroup>
          </Box>
          <Box className={classes.settingBox} style={{ visibility: twoAuth === 'allow' ? 'visible' : 'hidden' }}>
            <Box className={classes.textBox}>メールアドレス</Box>
            <OutlineTextBox
                id="email"
                name="email"
                value={email}
                label=""
                fullWidth
                onChange={handleChangeEmailInput}
                className={classes.textBox}
            />
          </Box>
          <Box className={classes.settingBox} style={{ visibility: twoAuth === 'allow' ? 'visible' : 'hidden' }}>
            <Box className={classes.textBox}>メールアドレス（確認）</Box>
            <OutlineTextBox
                id="email"
                name="email"
                value={confirmEmail}
                label=""
                fullWidth
                onChange={handleChangeConfirmEmailInput}
                className={classes.textBox}
            />
          </Box>
          <Box className={classes.settingBox}>
            <Box className={classes.passText}>確認のためあなたのパスワードを入力してください。</Box>
            <OutlineTextBox
                id="pass"
                name="pass"
                value={password}
                type="password"
                label=""
                fullWidth
                onChange={handleChangePasswordInput}
                className={classes.textBox}
            />
          </Box>
        </DialogContent>
        <DialogActions
          classes={{
            root: classes.action,
          }}>
          <NormalButton onClick={onUpdate} className={`${classes.buttonIcon} ${classes.buttonOk}`}>
            {okBtnLabel}
          </NormalButton>
          <NormalButton onClick={onCancel} className={`${classes.buttonIcon} ${classes.buttonCancel}`}>
            {cancelBtnLabel}
          </NormalButton>
        </DialogActions>
      </Dialog>
      <ConfirmPolicyDialog
        open={policyModal}
        onOk={handleOkUpdateConfirmDlg}
        onCancel={handlePolicyCancel}
        addClass={{
          contentText: classes.contentText,
        }}
        msg={MessageHelper.messageFormat(MSG.INF261)}
      />
      <InfoDialog
        open={openUpdateSuccess}
        msg={MessageHelper.messageFormat(MSG.INF262)}
        closeFunc={() => setOpenUpdateSuccess(false)}
      />
    </>
    
  );
};

export default InputTextDialog;
